/* eslint-disable jsx-a11y/no-static-element-interactions */
/*
 * File: ReceiptTable.tsx
 * Project: mdsi-template-app
 * -----
 * Last Modified: 11 Feb 2021 2:16:18 pm
 * -----
 * Copyright (c) 2021 Elegant Software Solutions All rights reserved.
 */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import { DeleteOutlined, EnterOutlined } from '@ant-design/icons';
import { Button, Col, Divider, Input, Menu, Modal, Row, Typography, message } from 'antd';
import TextArea from 'antd/lib/input/TextArea';
import { MessageType } from 'antd/lib/message';
import Table, { ColumnsType } from 'antd/lib/table';
import BinSelector from 'components/atoms/Inputs/BinSelector';
import { InlineStylesModel } from 'models/InlineStylesModel';
import { LinesModel } from 'models/LinesModel';
import { POStagedTableModel } from 'models/POStagedTableModel';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import {
  removeAllSerialNumbers,
  removeSelectedRows,
  removerSerialNumber,
  setSelectedKeysToDelete,
  setSelectedLine,
  setSelectedMenu,
  setSelectedToDelete,
  setSerialNumbers,
  setStagedTable,
  updateStagedQuantity
} from 'redux/poReceipts/po-receipts-slice';
import { useGetPurchaseOrderQuery } from 'redux/services/grannyBird/grannyBirdApi';
import { ReduxState } from 'redux/store';

const styles: InlineStylesModel = {
  link: {
    color: 'blue',
    cursor: 'pointer'
  },
  data: {
    fontWeight: 700
  },
  deleteButton: {
    paddingBottom: 7
  },
  divider: {
    height: '100%',
    color: 'grey',
    borderLeft: '1px solid rgba(0, 0, 0, .2)'
  },
  dividerColumn: {
    textAlign: 'center'
  },

  modalInput: {
    width: '100%'
  },
  textAreaRow: {
    paddingTop: 20
  },
  textArea: {
    minHeight: 210
  }
};

interface Params {
  PoReceiptId: string;
}

export const ReceiptTable = (): JSX.Element => {
  const { PoReceiptId } = useParams<Params>();

  const [selectedMenu, setSelectedMenuKey] = useState<string[]>(['Main']);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [quantityToReceive, setQuantityToReceive] = useState<string>('');
  const [binNumber, setBinNumber] = useState<string>('');
  const [serialInput, setSerialInput] = useState<string>('');
  const { data: selectedReceipt } = useGetPurchaseOrderQuery({ purchaseOrderId: PoReceiptId });

  console.log(binNumber, 'binNumber');

  const { allReceiptLines, selectedLine, stagedTable, lineNumberLoading, selectedToDelete, selectedKeysToDelete, serialNumbers } = useSelector((state: ReduxState) => state.poReceipts);

  const dispatch = useDispatch();

  const rowSelection = {
    onChange: (selectedRowKeys: any, selectedRows: any): void => {
      dispatch(setSelectedToDelete(selectedRows));
      dispatch(setSelectedKeysToDelete(selectedRowKeys));
    },
    preserveSelectedRowKeys: false,
    selectedRowKeys: !selectedToDelete.length ? [] : selectedKeysToDelete
  };

  const onMainClicked = (): void => {
    setSelectedMenuKey(['Main']);
    dispatch(setSelectedMenu('Main'));
    setQuantityToReceive('');
    dispatch(setSelectedLine(null));
  };
  const onStagedClicked = (): void => {
    setSelectedMenuKey(['Staged']);
    dispatch(setSelectedMenu('Staged'));
  };

  const onCloseModal = (): void => {
    setShowModal(false);
    setBinNumber('');
    setSerialInput('');
    setQuantityToReceive('');
    dispatch(setSelectedLine(null));
  };

  const onSerialInput = (e: any): void => {
    const serialInput = e.currentTarget.value as string;

    setSerialInput(serialInput.toUpperCase());
  };

  const handleBinNumber = (value: string | string[]): void => {
    setBinNumber(value as string);
  };

  const onQuantityChange = (e: any) => {
    setQuantityToReceive(e.currentTarget.value);
  };

  const onSubmitModal = (): void | MessageType => {
    const enteredQuantity = parseInt(quantityToReceive);
    const trimValue = serialInput.replace(/\n/g, ' ').trim();
    const replaceSpace = trimValue.includes(' ') ? trimValue.replace(/ /g, ',') : trimValue;
    const inputIds = replaceSpace.includes(',') ? replaceSpace.split(',').filter((value) => value.length) : [trimValue];

    if (selectedLine?.quantityOrdered && inputIds.length > selectedLine?.quantityOrdered) return message.error('You have exceeded the quantity ordered for this part');

    if (enteredQuantity === 0 || enteredQuantity < 0) message.error('You must enter a number greater than 0');
    else if (isNaN(enteredQuantity) && !selectedLine?.isSerializedItem) message.error('You must enter a numerical quantity');
    else if (selectedLine?.quantityOpen === 0 || (selectedLine?.quantityOpen as number) - (selectedLine?.quantityStaged as number) - (enteredQuantity as number) < 0)
      message.error('Not enough available');
    else {
      const avaiableQuantity = (selectedLine?.quantityOpen as number) - (selectedLine?.quantityStaged as number);

      let allSerialNumbersValid = true;
      let serialNumberLengthValid = true;

      if (!binNumber) {
        message.error('Bin location not set');

        return;
      }

      if (selectedLine && inputIds.length > selectedLine?.quantityOrdered) {
        message.error('Too many serials entered, cannot be more than quantity ordered');

        return;
      }

      if (selectedLine?.isSerializedItem) {
        inputIds.forEach((serialNumber) => {
          if (!serialNumber.match(/^[a-z0-9]+$/i)) allSerialNumbersValid = false;
          else if (serialNumber.length > 20) serialNumberLengthValid = false;
        });
      }

      if (inputIds.length > avaiableQuantity) {
        message.error('Too many serial numbers provided. Look at quantity to receive.');
      } else if (new Set(inputIds).size !== inputIds.length) {
        message.error('No duplicate serial numbers can exist');
      } else if (!allSerialNumbersValid) message.error('One or more serial numbers is not alpha-numeric');
      else if (!serialNumberLengthValid) message.error('Serial number length cannot exceed 20 characters');
      else {
        const foundItemUsed = stagedTable.find((receipt) => receipt.itemId === selectedLine?.itemId);

        const duplicateFound = {
          found: false,
          value: 'empty'
        };

        foundItemUsed !== undefined
          ? foundItemUsed.serialNumbers.forEach((serialNumber) => {
              if (inputIds.includes(serialNumber) && foundItemUsed.isSerialized) {
                duplicateFound.found = true;
                duplicateFound.value = serialNumber;
              }
            })
          : null;

        if (duplicateFound.found) {
          message.error(`The value ${duplicateFound.value} already exists in the staged table`);
        } else {
          const initializeTable = !stagedTable.length ? true : false;
          const lineAlreadyStaged = stagedTable.find((group) => group.itemId === selectedLine?.itemId);

          dispatch(setSerialNumbers(inputIds));

          dispatch(
            setStagedTable({
              line: selectedLine?.lineNumber as string,
              itemId: selectedLine?.itemId as string,
              owner: selectedLine?.owner as string,
              condition: selectedLine?.condition as string,
              disposition: selectedLine?.disposition as string,
              warehouse: selectedLine?.warehouse as string,
              binLocation: binNumber,
              serialNumbers: inputIds,
              quantity: selectedLine?.isSerializedItem ? `${inputIds.length}` : `${quantityToReceive}`,
              isSerialized: selectedLine?.isSerializedItem ? true : false
            })
          );

          dispatch(
            updateStagedQuantity({
              itemId: selectedLine?.itemId as string,
              quantity: selectedLine?.isSerializedItem ? (inputIds.length as number) : (enteredQuantity as number),
              initializeTable: initializeTable || lineAlreadyStaged === undefined ? true : false
            })
          );

          setShowModal(false);
          setBinNumber('');
          setSerialInput('');
          setSelectedMenuKey(['Staged']);
          dispatch(setSelectedMenu('Staged'));
        }
      }
    }
  };

  const columns: ColumnsType<LinesModel> = [
    {
      title: 'Line',
      dataIndex: 'lineNumber'
    },
    {
      title: 'Receive Item',
      align: 'center',
      render: (_, record): JSX.Element => {
        return (
          // eslint-disable-next-line jsx-a11y/no-static-element-interactions
          <Button
            icon={<EnterOutlined />}
            onClick={(): void => {
              dispatch(setSelectedLine(record));
              setShowModal(true);
            }}
          />
        );
      }
    },
    {
      title: 'Item Id',
      dataIndex: 'itemId'
    },
    {
      title: 'Owner',
      dataIndex: 'owner'
    },
    {
      title: 'Condition',
      dataIndex: 'condition'
    },
    {
      title: 'Disposition',
      dataIndex: 'disposition'
    },
    {
      title: 'Site',
      dataIndex: 'site'
    },

    {
      title: 'Warehouse',
      dataIndex: 'warehouse'
    },

    {
      title: 'Bin',
      dataIndex: 'binLocation'
    },
    {
      title: 'Qty. Ordered',
      dataIndex: 'quantityOrdered'
    },
    {
      title: 'Qty. Received',
      dataIndex: 'quantityReceived'
    },
    {
      title: 'Qty. Open',
      dataIndex: 'quantityOpen'
    },
    {
      title: 'Qty. Staged',
      dataIndex: 'quantityStaged'
    },
    {
      title: 'Direct Delivery',
      dataIndex: 'directDelivery',
      render: (directDelivery): string => {
        if (directDelivery) return 'YES';
        else return 'NO';
      }
    }
  ];

  const columns2: ColumnsType<POStagedTableModel> = [
    {
      title: 'PO Line',
      dataIndex: 'lineNumber'
    },
    {
      title: 'Item Id',
      dataIndex: 'itemId'
    },
    {
      title: 'Owner',
      dataIndex: 'owner'
    },
    {
      title: 'Condition',
      dataIndex: 'condition'
    },
    {
      title: 'Disposition',
      dataIndex: 'disposition'
    },
    {
      title: 'Warehouse',
      dataIndex: 'warehouse'
    },
    {
      title: 'Bin',
      dataIndex: 'binLocation'
    },
    {
      title: 'Serial Number',
      dataIndex: 'serialNumber'
    },
    {
      title: 'Qty',
      dataIndex: 'qtyDisplayed'
    },
    {
      title: 'Action',
      key: 'action',
      align: 'center',
      render: (_, record): JSX.Element => {
        return (
          <div
            style={styles.link}
            className="custom-link"
            onClick={(): void => {
              dispatch(removerSerialNumber(record.serialNumber));
              dispatch(removeSelectedRows([record]));
            }}>
            <DeleteOutlined style={{ color: 'red', fontSize: 16 }} />
          </div>
        );
      }
    }
  ];

  useEffect(() => {
    return (): void => {
      dispatch(removeAllSerialNumbers());
    };
  }, [dispatch]);

  // const isValidBinLocation = selectedLine?.location !== '' && typeof selectedLine?.location !== 'undefined' ? true : false;

  // isValidBinLocation === true && binNumber !== selectedLine?.location ? setBinNumber(selectedLine?.location as string) : null;

  const modalJSX = (
    <Modal width={700} centered visible={showModal} onCancel={onCloseModal} onOk={onSubmitModal}>
      <Row>
        <Col span={24}>
          <h1> Selected Item</h1>
        </Col>
        <Col span={10}>
          <Row justify="space-between">
            <Col>Purchase Order Number:</Col>
            <Col style={styles.data}>{selectedReceipt?.purchaseOrderNumber.length ? selectedReceipt?.purchaseOrderNumber : 'N/A '}</Col>
          </Row>
          <Row justify="space-between">
            <Col>Item Id:</Col>
            <Col style={styles.data}>{selectedLine?.itemId.length ? selectedLine?.itemId : 'N/A'}</Col>
          </Row>
          <Row justify="space-between">
            <Col>Warehouse</Col>
            <Col style={styles.data}>{selectedLine?.warehouse ?? (selectedReceipt?.warehouse || 'N/A')}</Col>
          </Row>
        </Col>
        <Col style={styles.dividerColumn} span={2}>
          <Divider style={styles.divider} type="vertical"></Divider>
        </Col>
        <Col span={10}>
          <Row justify="space-between">
            <Col>Qty. to Receive</Col>
            <Col style={styles.data}>{selectedLine?.quantityOpen}</Col>
          </Row>
          <Row justify="space-between">
            <Col>Bin Location:</Col>
            <Col span={24}>
              <BinSelector value={binNumber} onSelect={handleBinNumber} warehouseId={selectedLine?.warehouse ?? (selectedReceipt?.warehouse || 'N/A')} />
            </Col>
          </Row>
        </Col>
      </Row>
      <Row style={styles.textAreaRow}>
        <Typography.Title level={5}>Serial Numbers: </Typography.Title>
        <TextArea value={serialInput} onChange={onSerialInput} style={styles.textArea}></TextArea>
      </Row>
    </Modal>
  );
  const nonSerializedQuantityInputJSX = (
    <Col span={24}>
      <Input value={quantityToReceive} onChange={onQuantityChange}></Input>
    </Col>
  );
  const secondModal = (
    <Modal width={700} centered visible={showModal} onCancel={onCloseModal} onOk={onSubmitModal}>
      <Row>
        <Col span={24}>
          <h1>Selected Item</h1>
        </Col>
        <Col span={10}>
          <Row justify="space-between">
            <Col>Purchase Order Number:</Col>
            <Col style={styles.data}>{selectedReceipt?.purchaseOrderNumber.length ? selectedReceipt?.purchaseOrderNumber : 'N/A '}</Col>
          </Row>
          <Row justify="space-between">
            <Col>Item Id:</Col>
            <Col style={styles.data}>{selectedLine?.itemId.length ? selectedLine?.itemId : 'N/A'}</Col>
          </Row>
          <Row justify="space-between">
            <Col>Warehouse</Col>
            <Col style={styles.data}>{selectedReceipt?.warehouse.length ? selectedReceipt?.warehouse : 'N/A'}</Col>
          </Row>
        </Col>
        <Col style={styles.dividerColumn} span={2}>
          <Divider style={styles.divider} type="vertical"></Divider>
        </Col>
        <Col span={10}>
          <Row justify="space-between">
            <Col>Qty. to Receive</Col>
            {nonSerializedQuantityInputJSX}
          </Row>
          <Row justify="space-between">
            <Col>Bin Location:</Col>
            <BinSelector value={binNumber} onSelect={handleBinNumber} warehouseId={selectedLine?.warehouse ?? (selectedReceipt?.warehouse || 'N/A')} />
          </Row>
        </Col>
      </Row>
      <Row style={styles.textAreaRow}>
        <Typography.Title level={5}>Serial Numbers: </Typography.Title>
        <TextArea disabled value={serialInput} onChange={onSerialInput} style={styles.textArea}></TextArea>
      </Row>
    </Modal>
  );

  const menuFeatureJSX = (
    <Menu disabledOverflow={true} style={styles.menuTabPadding} selectedKeys={selectedMenu} mode="horizontal">
      <Menu.Item onClick={onMainClicked} key="Main">
        Main
      </Menu.Item>
      <Menu.Item style={{ width: 110 }} onClick={onStagedClicked} key="Staged">
        Staged ({serialNumbers.length})
      </Menu.Item>
    </Menu>
  );

  const stagedItems: POStagedTableModel[] = [];

  // HANDLE CASE WHERE MULT LINES WITH DIFFERENT BIN LOCATION ... RIGHT NOW MORE THAN ONE LINE WILL CAUSE ALL LINES TO SHARE SAME LOCATION

  stagedTable.forEach((group) => {
    console.log(group, 'group');
    group.serialNumbers.forEach((serialNumber, i) => {
      stagedItems.push({
        lineNumber: group.line,
        itemId: group.itemId as string,
        owner: group.owner as string,
        condition: group.condition as string,
        disposition: group.disposition as string,
        warehouse: group.warehouse as string,
        binLocation: group.binLocation as string,
        serialNumber: serialNumber,
        qty: selectedLine?.isSerializedItem ? '1' : group.quantity
      });
    });
  });

  const stagedLineItems = stagedItems.map((lineItem, index) => {
    const findLineItem = allReceiptLines?.find((line) => lineItem.lineNumber === line.lineNumber);
    const isSerialized = findLineItem !== undefined && findLineItem.isSerializedItem ? true : false;
    const useBinLocation = findLineItem !== undefined ? true : false;

    return { ...lineItem, ...{ key: index, qtyDisplayed: isSerialized ? 1 : lineItem.qty, bin: useBinLocation ? (findLineItem?.location as string) : lineItem.binLocation } };
  });

  const mainTableJSX = <Table style={{ width: '100%' }} columns={columns} dataSource={selectedReceipt?.lines} loading={lineNumberLoading} />;
  const stagedTableJSX = <Table style={{ width: '100%' }} rowSelection={rowSelection} columns={columns2} dataSource={stagedLineItems} />;

  const tableInView = selectedMenu[0] === 'Main' ? mainTableJSX : stagedTableJSX;

  useEffect(() => {
    if (selectedLine !== null) {
      setQuantityToReceive(`${selectedLine?.quantityOpen}`);
    }
  }, [selectedLine]);

  return (
    <>
      <Row justify="space-between" align="bottom">
        <Col>{menuFeatureJSX}</Col>
      </Row>
      <Row>{tableInView}</Row>
      {selectedLine?.isSerializedItem ? modalJSX : secondModal}
    </>
  );
};
